import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../Authentication/LanguageContext";
import "./A1.css";

interface Card {
  title: string;
  subtitle: string;
  badge: string;
  color: string;
  gradient: [string, string];
  pattern: string;
  route: string;
}

const A1: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const translations = {
    title: {
      en: 'A1 - Anfänger',
      de: 'A1 - Anfänger',
      ta: 'A1 - Anfänger',
      si: 'A1 - Anfänger',
      hi: 'A1 - Anfänger',
      ar: 'A1 - Anfänger',
      tr: 'A1 - Anfänger',
      ru: 'A1 - Anfänger',
      zh: 'A1 - Anfänger',
      fr: 'A1 - Anfänger',
      es: 'A1 - Anfänger',
    },
    subtitle: {
      en: 'Beginner Level',
      de: 'Anfängerlevel',
      ta: 'ஆரம்ப நிலை',
      si: 'ආරම්භක මට්ටම',
      hi: 'प्रारंभिक स्तर',
      ar: 'المستوى المبتدئ',
      tr: 'Başlangıç Seviyesi',
      ru: 'Начальный уровень',
      zh: '初学者水平',
      fr: 'Niveau débutant',
      es: 'Nivel principiante',
    },
    basic: {
      en: 'Learn basics',
      de: 'Grundlagen lernen',
      ta: 'அடிப்படை கற்றல்',
      si: 'මූලிக අධ්‍යාපනය',
      hi: 'बुनियादी सीखें',
      ar: 'تعلم الأساسيات',
      tr: 'Temelleri öğrenin',
      ru: 'Изучение основ',
      zh: '学习基础',
      fr: 'Apprendre les bases',
      es: 'Aprender lo básico',
    },
    conversations: {
      en: 'Practice talking',
      de: 'Gespräche üben',
      ta: 'பேசல் பயிற்சி',
      si: 'කථනය පුහුණු කරන්න',
      hi: 'बातचीत का अभ्यास करें',
      ar: 'ممارسة التحدث',
      tr: 'Konuşma pratiği yapın',
      ru: 'Практика разговоров',
      zh: '练习对话',
      fr: 'Pratiquez la conversation',
      es: 'Practicar hablar',
    },
    grammar: {
      en: 'Understand grammar',
      de: 'Grammatik verstehen',
      ta: 'வியக்கத்தைப் புரிந்துகொள்',
      si: 'විග්‍රහය තේරීම',
      hi: 'व्याकरण समझें',
      ar: 'فهم القواعد',
      tr: 'Dilbilgisi anlayın',
      ru: 'Понимание грамматики',
      zh: '理解语法',
      fr: 'Comprendre la grammaire',
      es: 'Entender la gramática',
    },
    stories: {
      en: 'Read simple stories',
      de: 'Einfache Geschichten lesen',
      ta: 'எளிய கதைகளைப் படிக்கவும்',
      si: 'සැහැල්ලු කතා කියවන්න',
      hi: 'सरल कहानियाँ पढ़ें',
      ar: 'قراءة القصص البسيطة',
      tr: 'Basit hikayeler okuyun',
      ru: 'Чтение простых историй',
      zh: '阅读简单故事',
      fr: 'Lire des histoires simples',
      es: 'Leer historias simples',
    },
  };

  const cards: Card[] = [
    { 
      title: "Grundlagen", 
      subtitle: translations.basic[language], 
      badge: "A1", 
      color: "#14B8A6", 
      gradient: ["#5EEAD4", "#2DD4BF"],
      pattern: "diagonal-stripes",
      route: "/levels/a1/basic"
    },
    { 
      title: "Gespräche", 
      subtitle: translations.conversations[language], 
      badge: "A1", 
      color: "#3B82F6", 
      gradient: ["#93C5FD", "#3B82F6"],
      pattern: "zigzag",
      route: "/levels/a1/a1conversations"
    },
    { 
      title: "Grammatik", 
      subtitle: translations.grammar[language], 
      badge: "A1", 
      color: "#EF4444", 
      gradient: ["#FCA5A5", "#EF4444"],
      pattern: "dots",
      route: "/levels/a1/a1grammar"
    },
    { 
      title: "Geschichten", 
      subtitle: translations.stories[language], 
      badge: "A1", 
      color: "#8B5CF6", 
      gradient: ["#C084FC", "#8B5CF6"],
      pattern: "grid",
      route: "/levels/a1/a1stories"
    },
  ];

  const handleClick = (route: string) => {
    if (route !== "#") {
      navigate(route);
    } else {
      alert("Coming soon!");
    }
  };

  return (
    <div className="a1-wrapper">
      <div className="a1-header">
        <h1 className="a1-title">{translations.title[language]}</h1>
        <p className="a1-subtitle">{translations.subtitle[language]}</p>
      </div>
      <div className="a1-grid">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`a1-card ${card.pattern}`}
            onClick={() => handleClick(card.route)}
            style={{
              background: `linear-gradient(135deg, ${card.gradient[0]}, ${card.gradient[1]})`,
              '--pattern-color': `${card.color}20`
            } as React.CSSProperties}
          >
            <div
              className="a1-badge"
              style={{ backgroundColor: card.color }}
            >
              {card.badge}
            </div>
            <div className="a1-info">
              <h2>{card.title}</h2>
              <p>{card.subtitle}</p>
            </div>
            <div className="a1-progress">
              <div className="progress-bar" style={{ width: `${(index + 1) * 25}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default A1;
